<script setup lang="ts">
import { useRemoveSaveElementalInAList } from '~~/src/composables/mutations/elemetal/useRemoveSaveElementalInAList'
import { useSaveElementalInAList } from '~~/src/composables/mutations/elemetal/useSaveElementalInAList'
import type { Elemental } from '~~/src/types/elemental'
import type { List } from '~~/src/types/list'
interface Props {
  list: List
  disabled?: boolean
  listIds?: string[]
  elemental?: Elemental | null
  type?: ElementalTypeEnum | null
  saveOnSelect?: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (name: 'selected', event: List): void
}>()
const listIds = toRef(props, 'listIds')
const elemental = toRef(props, 'elemental')
const elementalType = toRef(props, 'type')
const saveOnSelect = toRef(props, 'saveOnSelect')

const user = useUser()
const router = useRouter()
const toast = useToast()
const { mutate, isPending } = useSaveElementalInAList()
const { mutate: removeSave, isPending: isPendingRemove } = useRemoveSaveElementalInAList()
const isSaving = computed(() => isPending.value || isPendingRemove.value)

const handlerChange = async () => {
  if (!saveOnSelect.value)
    return

  const elementalRaw = unref(elemental)

  if (!elementalRaw || elementalType.value === undefined || elementalType.value === null)
    return
  if (!user.value) {
    const path = useGetLoginURL()
    router.push(path)
    return
  }
  const data = {
    id: elementalRaw.id,
    elementalType: elementalType.value,
    listId: props.list.id,
    slug: elementalRaw.url,
  }
  const lists = listIds.value ?? []
  if (lists.includes(props.list.id)) {
    removeSave(data, {
      onSuccess() {
        emit('selected', props.list)
      },
      onError: (error) => {
        console.error(error)
        return toast.warning(error.message, { id: 'save-error' })
      },
    })
    return
  }
  mutate(data, {
    onSuccess() {
      emit('selected', props.list)
    },
    onError: (error) => {
      console.error(error)
      return toast.warning(error.message, { id: 'save-error' })
    },
  })
}

const isSaved = computed(() => listIds.value?.includes(props.list?.id))
</script>

<template>
  <button
    :disabled="isSaving || disabled"
    type="button" role="checkbox"
    :aria-checked="isSaved"
    :title="list.title" :aria-label="list.title"
    :class="$style.container" class="cursor-pointer disabled:cursor-not-allowed disabled:opacity-80"
    @click="handlerChange"
  >
    <div
      :class="$style.avatar"
    >
      <img
        :src="list?.avatar ? optimizeImage(list?.avatar, { width: 40, height: 40 })
          : '/prompt_avatar.png'"
        class="w-10 h-10 border-1 border-soft-gray rounded-1"
      >
    </div>
    <div :class="$style.name">
      <p class="!hover:text-darkGray text-sm truncate text-softBlack font-700 capitalize font-inter ">
        {{ list.title }}
      </p>
    </div>
    <div :class="$style.profile">
      <img
        :src="optimizeImage(list?.user.avatar, { width: 16, height: 16 })"
        class="w-3 h-3 border-1 border-soft-gray rounded-1"
        aria-label="You profile picture"
      >
      {{ list.user.name }}
    </div>
    <div :class="$style.save">
      <div v-if="isSaved" i-mdi:check-circle flex-shrink-0 text-bluish-darker text="20px" />
      <div v-else i-mdi:circle-outline flex-shrink-0 text-darkGray text="20px" />
    </div>
  </button>
</template>

<style lang="scss" module>
.container{
 display: grid;
 grid-template-columns: auto 2fr auto;
 grid-template-areas:
    "avatar name name save"
    "avatar profile profile save";
  row-gap: 2px;
  column-gap: 12px;
  @apply py-1 w-full items-center ;
}
.save {
  grid-area: save;
  @apply flex items-center justify-end;

}
.profile {
  grid-area: profile;
  @apply flex gap-1 items-center text-xs font-inter font-bold text-darkGray;
}
.name {
  grid-area: name;
  @apply color-softBlack  w-full flex items-center justify-start;
}
.avatar {
  grid-area: avatar;
  @apply flex items-center justify-center;
}
</style>
